import { useStore } from '../store';
import { authApi } from '../services/api';
import {
  Settings,
  Save,
  FilePlus,
  LogOut,
  User,
  ChevronDown,
  PanelRight,
} from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import SubscriptionBadge from './SubscriptionBadge';

interface Props {
  onSettingsClick: () => void;
}

export default function Header({ onSettingsClick }: Props) {
  const {
    user,
    currentSessionName,
    hasUnsavedChanges,
    toggleSidebar,
    isSidebarOpen,
    saveCurrentSession,
    createNewSession,
    logout,
    addNotification,
    brandingSettings,
  } = useStore();

  const [showUserMenu, setShowUserMenu] = useState(false);
  const [saving, setSaving] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveCurrentSession();
    } catch {
      addNotification('Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authApi.logout();
      logout();
    } catch {
      // Still logout locally even if API fails
      logout();
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 h-16 bg-white border-b border-gray-200 z-30">
      <div className="h-full px-4 flex items-center justify-between">
        {/* Left side */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            {brandingSettings.customLogoEnabled && brandingSettings.logoPath ? (
              <img
                src={brandingSettings.logoPath}
                alt="Logo"
                className="h-8 w-auto object-contain"
                style={{ maxHeight: brandingSettings.logoSize ? `${brandingSettings.logoSize}px` : '32px' }}
              />
            ) : null}
            <h1 className="text-xl font-bold text-accent">
              {brandingSettings.companyName || 'DocWriter'}
            </h1>
          </div>
          <span className="text-gray-400">|</span>
          <span className="text-gray-700 max-w-[200px] truncate text-sm">
            {currentSessionName}
          </span>
          {hasUnsavedChanges && (
            <span className="w-2 h-2 bg-orange-400 rounded-full" title="Unsaved changes" />
          )}
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2">
          <button
            onClick={createNewSession}
            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors text-sm"
            title="New document (Ctrl+N)"
          >
            <FilePlus size={18} />
            <span className="hidden sm:inline">New</span>
          </button>

          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 text-sm"
            title="Save (Ctrl+S)"
          >
            <Save size={18} className={saving ? 'animate-pulse' : ''} />
            <span className="hidden sm:inline">Save</span>
          </button>

          <button
            onClick={onSettingsClick}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Settings"
          >
            <Settings size={20} />
          </button>

          {/* Sidebar Toggle */}
          <button
            onClick={toggleSidebar}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
              isSidebarOpen
                ? 'bg-accent/10 text-accent'
                : 'hover:bg-gray-100 text-gray-600'
            }`}
            title="Document Setup (Ctrl+B)"
          >
            <PanelRight size={18} />
            <span className="hidden sm:inline text-sm font-medium">Setup</span>
          </button>

          {/* Subscription Badge */}
          <SubscriptionBadge />

          {/* User menu */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center" style={{ color: 'var(--accent-text-color, #ffffff)' }}>
                <User size={16} />
              </div>
              <ChevronDown size={16} />
            </button>

            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
                <div className="px-4 py-2 border-b border-gray-100">
                  <p className="font-medium truncate text-sm">{user?.name}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-gray-50 text-red-600 text-sm"
                >
                  <LogOut size={16} />
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
